import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, GripVertical, Plus, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  reorderAnnouncements,
  type Announcement,
} from "@/lib/api";
import { useAnnouncements } from "@/lib/AnnouncementsContext";
import { AnnouncementColorPicker } from "./AnnouncementColorPicker";

// Full management surface for announcements (rename/delete), opened from the
// navbar like TaxonomyModal manages brands/categories. Kept as a separate
// component rather than generalizing TaxonomyModal since announcements need
// two fields (message/message_en) and a delete action that TaxonomyModal
// deliberately doesn't support.
export function AnnouncementsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { announcements: items, loading, upsert, remove, replaceAll } = useAnnouncements();

  // Message editing and color editing are independent per row now - each
  // opens straight from a single click (text -> inline input, swatch ->
  // color popover) instead of one click opening a row-wide "edit mode" that
  // a second click was then needed to actually reach the color picker from.
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editMessage, setEditMessage] = useState("");
  const [editMessageEn, setEditMessageEn] = useState("");
  const [savingMessageId, setSavingMessageId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [colorSavingId, setColorSavingId] = useState<string | null>(null);

  // Briefly shows a green check next to a row after its message saves.
  const [justSavedId, setJustSavedId] = useState<string | null>(null);
  const justSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Pointer-based reordering: dragging the grip never moves the row under the
  // cursor - instead the row takes its new slot as the pointer crosses its
  // neighbours, and the rows slide into place (FLIP, see the layout effect).
  // The list is saved optimistically on release, and rolled back if the
  // server rejects it.
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const prevTopsRef = useRef(new Map<string, number>());
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [liveOrder, setLiveOrder] = useState<string[] | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  // While a row is being dragged (or its new order is saving), the other row
  // controls fade and ignore input, same as the other grips.
  const reorderLocked = draggingId !== null || savingOrder;
  const [reorderError, setReorderError] = useState<string | null>(null);

  const displayedItems = liveOrder
    ? liveOrder
        .map(id => items.find(a => a.id === id))
        .filter((a): a is Announcement => a !== undefined)
    : items;

  // FLIP: after every reorder, move each row back to where it was (inverted
  // transform), then let it transition to its new slot. offsetTop is the
  // layout position, unaffected by in-flight transforms.
  useLayoutEffect(() => {
    const prevTops = prevTopsRef.current;
    const nextTops = new Map<string, number>();
    rowRefs.current.forEach((el, id) => {
      const top = el.offsetTop;
      nextTops.set(id, top);
      const prev = prevTops.get(id);
      if (prev === undefined || prev === top) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${prev - top}px)`;
      void el.offsetHeight;
      el.style.transition = "transform 200ms ease";
      el.style.transform = "";
    });
    prevTopsRef.current = nextTops;
  });

  // Window-level listeners rather than pointer capture on the grip: React
  // reorders the rows by moving their DOM nodes, and the browser drops the
  // capture (and the pointerup) when the captured node is moved.
  const liveOrderRef = useRef<string[] | null>(null);
  const draggingIdRef = useRef<string | null>(null);

  const startDrag = (e: React.PointerEvent<HTMLElement>, id: string) => {
    // One drag at a time, and none while the previous order is still saving.
    if (e.button !== 0 || draggingIdRef.current || savingOrder) return;
    e.preventDefault();
    const order = items.map(a => a.id);
    liveOrderRef.current = order;
    draggingIdRef.current = id;
    setLiveOrder(order);
    setDraggingId(id);
  };

  const finishDrag = async () => {
    const order = liveOrderRef.current;
    liveOrderRef.current = null;
    draggingIdRef.current = null;
    setDraggingId(null);
    setLiveOrder(null);
    if (!order) return;

    const previous = items;
    if (order.every((id, index) => previous[index]?.id === id)) return;

    const byId = new Map(previous.map(a => [a.id, a]));
    replaceAll(order.map((id, index) => ({ ...byId.get(id)!, position: index })));
    setReorderError(null);
    setSavingOrder(true);
    try {
      await reorderAnnouncements(order);
      // Home is ordered by announcement position server-side, so drop its
      // cached list instead of waiting out staleTime.
      void queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
    } catch (err) {
      replaceAll(previous);
      setReorderError(err instanceof Error ? err.message : t("announcements.couldNotReorder"));
    } finally {
      setSavingOrder(false);
    }
  };

  const finishDragRef = useRef(finishDrag);
  finishDragRef.current = finishDrag;

  useEffect(() => {
    if (!draggingId) return;

    const onMove = (e: PointerEvent) => {
      const list = listRef.current;
      const order = liveOrderRef.current;
      const dragged = draggingIdRef.current;
      if (!list || !order || !dragged) return;

      const y = e.clientY - list.getBoundingClientRect().top + list.scrollTop;
      // Going up swaps early (15% into the row above) so it isn't left behind;
      // going down swaps once the pointer passes the middle of the row below.
      const next = order.slice();
      let index = next.indexOf(dragged);
      const start = index;
      while (index > 0) {
        const el = rowRefs.current.get(next[index - 1]!);
        if (!el || y >= el.offsetTop + el.offsetHeight * 0.85) break;
        [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
        index--;
      }
      while (index === start && index < next.length - 1) {
        const el = rowRefs.current.get(next[index + 1]!);
        if (!el || y <= el.offsetTop + el.offsetHeight * 0.5) break;
        [next[index + 1], next[index]] = [next[index]!, next[index + 1]!];
        index++;
      }
      if (index === start) return;
      liveOrderRef.current = next;
      setLiveOrder(next);
    };
    const onEnd = () => void finishDragRef.current();

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    window.addEventListener("blur", onEnd);
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      window.removeEventListener("blur", onEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [draggingId]);

  const [newMessage, setNewMessage] = useState("");
  const [newMessageEn, setNewMessageEn] = useState("");
  const [newBackgroundColor, setNewBackgroundColor] = useState<string | null>(null);
  const [newTextColor, setNewTextColor] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => () => {
    if (justSavedTimeoutRef.current) clearTimeout(justSavedTimeoutRef.current);
  }, []);

  const startEditMessage = (item: Announcement) => {
    setEditingMessageId(item.id);
    setEditMessage(item.message);
    setEditMessageEn(item.message_en ?? "");
    setEditError(null);
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditError(null);
  };

  const saveMessage = async (id: string) => {
    const trimmed = editMessage.trim();
    if (!trimmed) {
      setEditError(t("common.required"));
      return;
    }

    const current = items.find(item => item.id === id);
    if (!current) return;

    setSavingMessageId(id);
    setEditError(null);
    try {
      const updated = await updateAnnouncement(id, trimmed, editMessageEn.trim() || null, {
        backgroundColor: current.background_color,
        textColor: current.text_color,
      });
      upsert(updated);
      setEditingMessageId(null);

      if (justSavedTimeoutRef.current) clearTimeout(justSavedTimeoutRef.current);
      setJustSavedId(id);
      justSavedTimeoutRef.current = setTimeout(() => setJustSavedId(null), 1500);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : t("taxonomy.couldNotUpdate"));
    } finally {
      setSavingMessageId(null);
    }
  };

  // Colors save immediately on every change - like AnnouncementPicker's
  // inline color editor - since there's no row-wide "edit mode" gating them
  // behind a separate save step anymore.
  const changeItemColors = async (
    item: Announcement,
    colors: { backgroundColor: string | null; textColor: string | null }
  ) => {
    setColorSavingId(item.id);
    try {
      const updated = await updateAnnouncement(item.id, item.message, item.message_en, colors);
      upsert(updated);
    } catch {
      // Best-effort - the popover has no room for inline error feedback.
    } finally {
      setColorSavingId(null);
    }
  };

  const removeItem = async (id: string) => {
    if (!confirm(t("announcements.confirmDelete"))) return;

    setDeletingId(id);
    setDeleteError(null);
    try {
      await deleteAnnouncement(id);
      remove(id);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t("announcements.couldNotDelete"));
    } finally {
      setDeletingId(null);
    }
  };

  const addItem = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed) return;

    setAdding(true);
    setAddError(null);
    try {
      const created = await createAnnouncement(trimmed, newMessageEn.trim() || null, {
        backgroundColor: newBackgroundColor,
        textColor: newTextColor,
      });
      upsert(created);
      setNewMessage("");
      setNewMessageEn("");
      setNewBackgroundColor(null);
      setNewTextColor(null);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t("taxonomy.couldNotAdd"));
    } finally {
      setAdding(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-app-surface text-foreground border border-app-border rounded-2xl shadow-xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between bg-brand text-white px-5 py-4">
          <h2 className="text-lg font-bold">{t("announcements.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/20 transition-colors duration-200 cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        <div ref={listRef} className="relative flex flex-col gap-3 p-5 max-h-[65vh] overflow-y-auto">
          {loading && <p className="text-sm text-app-muted">{t("taxonomy.loading")}</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-app-muted">{t("announcements.noAnnouncements")}</p>
          )}
          {deleteError && <p className="text-sm text-red-500">{deleteError}</p>}
          {reorderError && <p className="text-sm text-red-500">{reorderError}</p>}

          {!loading &&
            displayedItems.map(item => (
              <div
                key={item.id}
                ref={el => {
                  if (el) rowRefs.current.set(item.id, el);
                  else rowRefs.current.delete(item.id);
                }}
                className={`flex flex-col gap-1 rounded-lg ${
                  draggingId === item.id ? "relative z-10 shadow-lg ring-2 ring-brand" : ""
                }`}
              >
                <div className="flex items-center gap-2 border border-app-border rounded-lg px-3 py-2">
                  {editingMessageId === item.id ? (
                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                      <input
                        autoFocus
                        type="text"
                        value={editMessage}
                        onChange={e => setEditMessage(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveMessage(item.id);
                          if (e.key === "Escape") cancelEditMessage();
                        }}
                        placeholder={t("announcements.messagePlaceholder")}
                        className="w-full bg-transparent outline-none text-sm text-foreground"
                      />
                      <input
                        type="text"
                        value={editMessageEn}
                        onChange={e => setEditMessageEn(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveMessage(item.id);
                          if (e.key === "Escape") cancelEditMessage();
                        }}
                        placeholder={t("announcements.messageEnPlaceholder")}
                        className="w-full bg-transparent outline-none text-sm text-app-muted"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditMessage(item)}
                      title={t("common.edit")}
                      className="flex-1 min-w-0 flex flex-col text-left cursor-pointer group/text"
                    >
                      <span className="truncate text-sm font-medium text-foreground group-hover/text:text-brand transition-colors duration-150">
                        {item.message}
                      </span>
                      {item.message_en && (
                        <span className="truncate text-xs text-app-muted">{item.message_en}</span>
                      )}
                    </button>
                  )}

                  {editingMessageId === item.id ? (
                    <button
                      type="button"
                      onClick={cancelEditMessage}
                      disabled={savingMessageId === item.id}
                      className="p-1.5 rounded-lg text-app-muted hover:bg-app-surface-hover transition-colors duration-200 cursor-pointer disabled:opacity-50"
                    >
                      <X size={16} />
                    </button>
                  ) : (
                    justSavedId === item.id && <Check size={16} className="shrink-0 text-green-600" />
                  )}

                  <span
                    className={`transition-opacity ${reorderLocked ? "opacity-30 pointer-events-none" : ""}`}
                  >
                    <AnnouncementColorPicker
                      backgroundColor={item.background_color}
                      textColor={item.text_color}
                      onChange={colors => changeItemColors(item, colors)}
                      disabled={colorSavingId === item.id || reorderLocked}
                    />
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    disabled={deletingId === item.id || reorderLocked}
                    className={`p-1.5 rounded-lg text-app-muted hover:text-red-500 hover:bg-red-500/10 transition-[color,background-color,opacity] duration-200 cursor-pointer ${
                      reorderLocked ? "opacity-30 pointer-events-none" : "disabled:opacity-50"
                    }`}
                  >
                    <Trash2 size={16} />
                  </button>
                  <span
                    onPointerDown={e => startDrag(e, item.id)}
                    title={t("announcements.dragToReorder")}
                    aria-label={t("announcements.dragToReorder")}
                    className={`shrink-0 p-1 -mr-1 touch-none select-none transition-opacity ${
                      draggingId === item.id
                        ? "text-brand cursor-grabbing"
                        : reorderLocked
                          ? "text-app-muted opacity-30 pointer-events-none"
                          : "text-app-muted hover:text-foreground cursor-grab"
                    }`}
                  >
                    <GripVertical size={16} />
                  </span>
                </div>
                {editingMessageId === item.id && editError && (
                  <span className="text-xs text-red-500 px-1">{editError}</span>
                )}
              </div>
            ))}

          <div className="flex flex-col gap-1 border border-dashed border-app-border rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") addItem();
                  }}
                  placeholder={t("announcements.messagePlaceholder")}
                  className="w-full bg-transparent outline-none text-sm text-foreground placeholder:text-app-muted"
                />
                <input
                  type="text"
                  value={newMessageEn}
                  onChange={e => setNewMessageEn(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") addItem();
                  }}
                  placeholder={t("announcements.messageEnPlaceholder")}
                  className="w-full bg-transparent outline-none text-sm text-app-muted placeholder:text-app-muted"
                />
              </div>
              <AnnouncementColorPicker
                backgroundColor={newBackgroundColor}
                textColor={newTextColor}
                onChange={({ backgroundColor, textColor }) => {
                  setNewBackgroundColor(backgroundColor);
                  setNewTextColor(textColor);
                }}
              />
              <button
                type="button"
                onClick={addItem}
                disabled={adding || !newMessage.trim()}
                className="p-1.5 rounded-lg bg-brand text-white hover:bg-brand/90 transition-colors duration-200 cursor-pointer disabled:opacity-50"
              >
                <Plus size={16} />
              </button>
            </div>
            {addError && <span className="text-xs text-red-500 px-1">{addError}</span>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
